import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import axios from "axios";
import JournalArticle from "@/pages/JournalArticle";
import CastlePage from "@/pages/CastlePage";
import FishersCrossPage from "@/pages/FishersCrossPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── image urls ── */
const IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/hrriw2lp_image.png",
  about: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/02gig7d0_castlefreke%20postcard.jpg",
  postcard: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/02gig7d0_castlefreke%20postcard.jpg",
  castle: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/mpaoog5l_image.png",
  lighthouse: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/3uzwx9nl_image.png",
  longStrand: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/ojp9n6yt_DJI_0333.jpg",
  forest: "https://images.unsplash.com/photo-1726428755646-be185fe76a49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxmb3Jlc3QlMjBuYXR1cmUlMjBwYXRofGVufDB8fHx8MTc3NjUwMTg2N3ww&ixlib=rb-4.1.0&q=85",
  redStrand: "https://images.unsplash.com/photo-1667828352513-ddcc4cea4c26?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxpcmVsYW5kJTIwY29hc3R8ZW58MHx8fHwxNzc2NTAxODY3fDA&ixlib=rb-4.1.0&q=85",
  coast: "https://images.unsplash.com/photo-1707409028181-23922fd54c71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxpcmVsYW5kJTIwY29hc3R8ZW58MHx8fHwxNzc2NTAxODY3fDA&ixlib=rb-4.1.0&q=85",
  heroOld: "https://images.unsplash.com/photo-1718121150898-0253f130caf4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxpcmVsYW5kJTIwY29hc3R8ZW58MHx8fHwxNzc2NTAxODY3fDA&ixlib=rb-4.1.0&q=85",
  goldenHour: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/37devt2k_DSCF5604%20%282015_06_02%2011_34_08%20UTC%29.JPG",
  fishBasket: "https://images.pexels.com/photos/18946627/pexels-photo-18946627.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  pub: "https://images.pexels.com/photos/35004395/pexels-photo-35004395.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  restaurant: "https://images.pexels.com/photos/29163295/pexels-photo-29163295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const EATDRINK = [
  { name: "The Fish Basket", type: "Seafood", location: "Long Strand Beach", desc: "Fresh fish and chips right on the sand. Peter and Elaine Shanahan\u2019s legendary rustic hut at the entrance to Long Strand \u2014 locally sourced West Cork seafood, generous portions, and the Atlantic as your backdrop. First come, first served.", phone: "023 885 1716", link: "https://thefishbasket.ie/", img: "fishBasket" },
  { name: "O\u2019Donovan\u2019s at Fisher\u2019s Cross", type: "Traditional Pub", location: "Ardfield, nr Castlefreke", desc: "A proper old-school West Cork pub. The O\u2019Donovan family have kept this crossroads bar for five generations \u2014 a place where locals gather, stories are told, and a pint of Murphy\u2019s tastes exactly as it should. Trad sessions every Thursday.", link: "/fishers-cross", img: "pub" },
  { name: "The Mountain House", type: "Bar & Restaurant", location: "Ardfield, nr Clonakilty", desc: "Country restaurant with character. Seafood, steaks, and honest cooking in a warm, unhurried setting. Open Thursday to Sunday, with outdoor seating in summer and views across the West Cork countryside.", link: "https://www.facebook.com/TheMountainHouseBar/", img: "restaurant" },
];

const ACTIVITIES = [
  { tag: "Beach", title: "Long Strand", duration: "Half day", desc: "A mile of wild Atlantic sand", detail: "An uninterrupted sweep of golden sand backed by ancient dunes and the Kennedy Pier. Home to sunset surfers, dog-walkers and the hush of the wild Atlantic.", img: IMAGES.longStrand },
  { tag: "History", title: "Castlefreke Castle", duration: "1 hour", desc: "A castle reborn from stone and story", detail: "The ancestral seat of the Evans-Freke family, now undergoing a remarkable restoration. A place where centuries of history meet extraordinary craftsmanship and a Wall Street banker\u2019s promise kept.", img: IMAGES.castle, link: "/the-castle" },
  { tag: "Nature", title: "Rathbarry Forest", duration: "2 hours", desc: "A living cathedral of oak and beech", detail: "Looping trails through Coillte forest, past the old walled garden and into clearings where deer browse in the early morning light.", img: IMAGES.forest },
  { tag: "Coast", title: "Galley Head Lighthouse", duration: "1\u20132 hours", desc: "A beacon above the cliffs", detail: "One of Ireland\u2019s most beautifully sited lighthouses, standing white and patient above a coastline of sea stacks, seabirds and Atlantic weather.", img: IMAGES.lighthouse, imgPos: "top left" },
  { tag: "Beach", title: "Red Strand", duration: "1\u20132 hours", desc: "A quiet cove of red-tinted sand", detail: "Around the headland from Long Strand, this smaller, sheltered beach is perfect for a morning swim or a flask of tea on the rocks.", img: IMAGES.redStrand },
  { tag: "History", title: "Rathbarry Church Ruins", duration: "30 min", desc: "A churchyard keeping watch over the fields", detail: "A small roofless church with weathered gravestones, wildflowers between the stones and views down to the Atlantic.", img: IMAGES.heroOld },
];

const VISIT_INFO = [
  { num: "01", label: "Nearest town", text: "Clonakilty \u2014 12 km east" },
  { num: "02", label: "Getting here", text: "By car from Cork Airport: 1 hr via N71. Limited local buses from Clonakilty." },
  { num: "03", label: "Best time to visit", text: "May to October for long days; September for surfers; winter for wild drama." },
  { num: "04", label: "Where to stay", text: "Camping at Sextons, B&Bs in Rosscarbery and Clonakilty, self-catering in Owenahincha." },
  { num: "05", label: "Eat & drink", text: "The Fish Basket on Long Strand, O\u2019Donovan\u2019s at Fisher\u2019s Cross, The Mountain House in Ardfield. Plus Pilgrim\u2019s in Rosscarbery and pints in De Barra\u2019s, Clonakilty." },
  { num: "06", label: "Pack for", text: "All four seasons in one day. Walking shoes, a waterproof, and a flask." },
];

const JOURNAL = [
  { slug: "the-light-between-storms", date: "13 Oct 2025", read: "4 min read", title: "The light between storms", excerpt: "Why October on Long Strand is West Cork\u2019s best kept secret \u2014 low sun, empty sand, and an ocean in every mood.", author: "The Keeper" },
  { slug: "walking-with-ruins", date: "02 Aug 2025", read: "6 min read", title: "Walking with ruins", excerpt: "A slow afternoon through Rathbarry Forest, past the walled garden to the skeleton of a castle.", author: "Aoife Murphy" },
  { slug: "a-night-at-galley-head", date: "20 May 2025", read: "5 min read", title: "A night at Galley Head", excerpt: "Staying in the lightkeeper\u2019s cottage, where the lantern still turns and the Atlantic keeps time.", author: "S\u00e9amus Walsh" },
];

const GALLERY = [
  { src: IMAGES.longStrand, label: "Beach" },
  { src: IMAGES.castle, label: "Castle" },
  { src: IMAGES.forest, label: "Forest" },
  { src: IMAGES.lighthouse, label: "Lighthouse" },
  { src: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/z6abs7hr_2026-1.jpg", label: "Sunset View" },
  { src: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/i6gmqeyf_20190818_193822.jpg", label: "Cliff Edge" },
  { src: IMAGES.goldenHour, label: "Golden Hour" },
  { src: IMAGES.redStrand, label: "Beach" },
  { src: IMAGES.hero, label: "Sunset" },
];

const MARQUEE_ITEMS = ["Long Strand", "Castlefreke Castle", "Rathbarry Forest", "Galley Head", "Red Strand", "Wild Atlantic Way", "Cuan Dor", "Rosscarbery"];

const FILTER_TABS = ["All", "Beach", "History", "Nature", "Coast"];

/* ── Nav ── */
function Nav({ scrolled }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const links = ["about","things","gallery","map","visit","eatdrink","journal"];
  const labels = ["About","Things to Do","Gallery","Map","Visit","Eat & Drink","Journal"];

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <nav className={`site-nav ${scrolled ? "nav-scrolled" : ""}`} data-testid="main-nav">
      <div className="nav-inner">
        <button className="nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="nav-logo">
          {scrolled ? <>Castlefreke<span className="nav-logo-sub">&nbsp;&amp; Long Strand</span></> : "Castlefreke"}
        </button>
        <div className={`nav-links ${mobileOpen ? "nav-open" : ""}`}>
          {links.map((id, i) => (
            <button key={id} className="nav-link" onClick={() => scrollTo(id)} data-testid={`nav-${id}`}>{labels[i]}</button>
          ))}
        </div>
        <span className="nav-right" data-testid="nav-location">West Cork &middot; Éire</span>
        <button className="nav-burger" onClick={() => setMobileOpen(!mobileOpen)} data-testid="nav-burger" aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </div>
    </nav>
  );
}

/* ── Hero ── */
function Hero() {
  return (
    <section className="hero" data-testid="hero-section">
      <div className="hero-bg" style={{ backgroundImage: `url(${IMAGES.hero})` }} />
      <div className="hero-overlay" />
      <div className="hero-content">
        <p className="hero-meta" data-testid="hero-meta">West Cork &middot; Ireland &middot; 51.55&deg; N</p>
        <h1 className="hero-title" data-testid="hero-title">
          Where the forest<br /><em>meets the sea.</em>
        </h1>
        <p className="hero-sub" data-testid="hero-subtitle">
          A wild mile of Atlantic sand, a castle being brought back to life, and the slow rhythm of a West Cork village that time seems to have kept for itself.
        </p>
        <button className="hero-cta" onClick={() => document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-cta">
          Begin the journey <span className="cta-arrow">&darr;</span>
        </button>
      </div>
      <div className="hero-bottom">
        <span className="hero-bottom-left">N&deg; 01 &mdash; Long Strand</span>
        <span className="hero-bottom-center">Rosscarbery &middot; Rathbarry &middot; Galley Head</span>
        <span className="hero-bottom-right">Est. 1780 &middot; Ivy &amp; stone</span>
      </div>
    </section>
  );
}

/* ── Marquee ── */
function Marquee() {
  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="marquee-wrap" data-testid="marquee">
      <div className="marquee-track">
        {items.map((t, i) => (
          <span key={i} className="marquee-item">{t}<span className="marquee-dot">&#10022;</span></span>
        ))}
      </div>
    </div>
  );
}

/* ── About ── */
function About() {
  return (
    <section id="about" className="section about" data-testid="about-section">
      <p className="section-num">N&deg; 02 &mdash; About the place</p>
      <h2 className="section-heading">A quiet bend in the<br />Wild Atlantic Way.</h2>
      <div className="about-grid">
        <div className="about-img-wrap">
          <img src={IMAGES.about} alt="Long Strand, Castlefreke" className="about-img" loading="lazy" />
        </div>
        <div className="about-text">
          <p>Castlefreke is a hidden treasure on the south-west coast of Ireland &mdash; a scatter of cottages, a forest, a graveyard, and one of the longest, most relaxing beaches in the country. The Evans-Freke family arrived in the 17th century and built their castle on a bluff with views to the Fastnet Lighthouse. After decades of neglect, a descendant &mdash; a retired Wall Street banker &mdash; bought it back at the turn of the millennium and began an extraordinary restoration, pouring years of craft and care into bringing the stone back to life.</p>
          <p>Long Strand runs for over a mile with the Atlantic on one side and a ridge of wildflower dunes on the other. Behind it, Rathbarry Forest climbs into oak and beech, sheltering the castle and the walled garden that once fed a country house.</p>
          <p>There are no neon signs here. No crowds. Just the tide, the terns, and a sense of time slowing down as you walk.</p>
        </div>
      </div>
      <div className="about-stats" data-testid="about-stats">
        <div className="stat"><span className="stat-val">1.6 km</span><span className="stat-label">Beach length</span></div>
        <div className="stat"><span className="stat-val">220 ha</span><span className="stat-label">Forest area</span></div>
        <div className="stat"><span className="stat-val">1780</span><span className="stat-label">Est. castle built</span></div>
      </div>
    </section>
  );
}

/* ── Things to Do ── */
function ThingsToDo() {
  const [filter, setFilter] = useState("All");
  const filtered = filter === "All" ? ACTIVITIES : ACTIVITIES.filter(a => a.tag === filter);

  return (
    <section id="things" className="section things" data-testid="things-section">
      <p className="section-num">N&deg; 03 &mdash; Things to do</p>
      <h2 className="section-heading">Six unhurried ways to wander.</h2>
      <div className="filter-tabs" data-testid="filter-tabs">
        {FILTER_TABS.map(t => (
          <button key={t} className={`filter-tab ${filter === t ? "active" : ""}`} onClick={() => setFilter(t)} data-testid={`filter-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>
      <div className="things-grid">
        {filtered.map((a, i) => {
          const Wrapper = a.link ? Link : "div";
          const wrapProps = a.link ? { to: a.link, className: "thing-card", "data-testid": `thing-card-${i}` } : { className: "thing-card", "data-testid": `thing-card-${i}` };
          return (
            <Wrapper key={i} {...wrapProps}>
              <div className="thing-img-wrap">
                <img src={a.img} alt={a.title} className="thing-img" loading="lazy" style={a.imgPos ? { objectPosition: a.imgPos } : undefined} />
                <span className="thing-tag">{a.tag}</span>
              </div>
              <div className="thing-body">
                <div className="thing-header">
                  <strong>{a.title}</strong>
                  <span className="thing-dur">{a.duration}</span>
                </div>
                <p className="thing-desc">{a.desc}</p>
                <p className="thing-detail">{a.detail}</p>
              </div>
            </Wrapper>
          );
        })}
      </div>
    </section>
  );
}

/* ── Gallery ── */
function GallerySection() {
  const [lightbox, setLightbox] = useState(null);

  return (
    <section id="gallery" className="section gallery" data-testid="gallery-section">
      <p className="section-num">N&deg; 04 &mdash; The Gallery</p>
      <h2 className="section-heading">Seen from the coast road.</h2>
      <p className="section-sub">A slow scroll through the textures of Castlefreke &mdash; sand, stone, ivy, cloud. Tap any frame to open.</p>
      <div className="gallery-grid">
        {GALLERY.map((g, i) => (
          <button key={i} className="gallery-item" onClick={() => setLightbox(i)} data-testid={`gallery-item-${i}`}>
            <img src={g.src} alt={g.label} className="gallery-img" loading="lazy" />
            <span className="gallery-label">{g.label}</span>
          </button>
        ))}
      </div>
      {lightbox !== null && (
        <div className="lightbox" onClick={() => setLightbox(null)} data-testid="lightbox">
          <img src={GALLERY[lightbox].src} alt={GALLERY[lightbox].label} className="lightbox-img" />
          <button className="lightbox-close" onClick={() => setLightbox(null)} data-testid="lightbox-close">&times;</button>
        </div>
      )}
    </section>
  );
}

/* ── Map ── */
function MapSection() {
  return (
    <section id="map" className="section map-section" data-testid="map-section">
      <p className="section-num">N&deg; 05 &mdash; Lay of the land</p>
      <h2 className="section-heading">Everything within an afternoon&rsquo;s walk.</h2>
      <p className="section-sub">A small world, close-knit and layered &mdash; from Rathbarry to Red Strand, Rosscarbery to Galley Head.</p>
      <div className="map-container">
        <iframe
          className="map-iframe"
          title="Castlefreke & Long Strand area map"
          src="https://www.openstreetmap.org/export/embed.html?bbox=-9.13%2C51.535%2C-8.955%2C51.605&amp;layer=mapnik&amp;marker=51.5655%2C-9.0477"
          data-testid="map-iframe"
        />
        <div className="map-links">
          <a href="https://www.openstreetmap.org/#map=14/51.5655/-9.0477" target="_blank" rel="noopener noreferrer" className="map-link" data-testid="map-link">View larger map &rarr;</a>
        </div>
      </div>
      <div className="map-points-below">
        <div className="map-point"><span className="mp-name">Long Strand</span><span className="mp-info">1.6 km of Atlantic sand</span></div>
        <div className="map-point"><span className="mp-name">Castlefreke Castle</span><span className="mp-info">Restoration in progress</span></div>
        <div className="map-point"><span className="mp-name">Rathbarry Forest</span><span className="mp-info">220 ha of oak &amp; beech</span></div>
        <div className="map-point"><span className="mp-name">Red Strand</span><span className="mp-info">Sheltered cove</span></div>
        <div className="map-point"><span className="mp-name">Rosscarbery</span><span className="mp-info">Nearest village</span></div>
        <div className="map-point"><span className="mp-name">Galley Head</span><span className="mp-info">Lighthouse</span></div>
      </div>
    </section>
  );
}

/* ── Visit ── */
function VisitSection() {
  return (
    <section id="visit" className="section visit" data-testid="visit-section">
      <p className="section-num">N&deg; 06 &mdash; Plan your visit</p>
      <h2 className="section-heading">A small field guide.</h2>
      <p className="section-sub">Practical notes from people who know the place. Wear layers. Mind the tide. Say hello in the shop.</p>
      <ol className="visit-list">
        {VISIT_INFO.map(v => (
          <li key={v.num} className="visit-item" data-testid={`visit-item-${v.num}`}>
            <span className="visit-num">{v.num}</span>
            <div className="visit-body">
              <span className="visit-label">{v.label}</span>
              <span className="visit-text">{v.text}</span>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

/* ── Journal ── */
function JournalSection() {
  return (
    <section id="journal" className="section journal" data-testid="journal-section">
      <p className="section-num">N&deg; 07 &mdash; Journal</p>
      <h2 className="section-heading">Letters from the coast.</h2>
      <p className="section-sub">Occasional writings about storms, walks, wildlife and the small news of the parish.</p>
      <ul className="journal-list">
        {JOURNAL.map((j, i) => (
          <li key={i} className="journal-item" data-testid={`journal-item-${i}`}>
            <a href={`/journal/${j.slug}`} className="journal-link" data-testid={`journal-link-${i}`}>
              <div className="journal-meta">
                <span>{j.date}</span>
                <span className="journal-read">{j.read}</span>
              </div>
              <h3 className="journal-title">{j.title}</h3>
              <p className="journal-excerpt">{j.excerpt}</p>
              <span className="journal-author">by {j.author}</span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ── Eat & Drink ── */
function EatDrinkSection() {
  return (
    <section id="eatdrink" className="section eatdrink" data-testid="eatdrink-section">
      <p className="section-num">N&deg; 08 &mdash; Eat &amp; Drink</p>
      <h2 className="section-heading">Three places worth<br />knowing about.</h2>
      <p className="section-sub">The food around here is honest, local, and served without fuss. A fish supper on the sand, a pint by the fire, or a proper dinner with a view.</p>
      <div className="eatdrink-grid">
        {EATDRINK.map((place, i) => (
          <div key={i} className="eatdrink-card" data-testid={`eatdrink-card-${i}`}>
            <div className="eatdrink-img-wrap">
              <img src={IMAGES[place.img]} alt={place.name} className="eatdrink-img" loading="lazy" />
              <span className="eatdrink-type">{place.type}</span>
            </div>
            <div className="eatdrink-body">
              <h3 className="eatdrink-name">{place.name}</h3>
              <p className="eatdrink-loc">{place.location}</p>
              <p className="eatdrink-desc">{place.desc}</p>
              <div className="eatdrink-footer">
                {place.phone && <span className="eatdrink-phone">{place.phone}</span>}
                {place.link && place.link.startsWith("/") ? (
                  <Link to={place.link} className="eatdrink-link" data-testid={`eatdrink-link-${i}`}>Read more &rarr;</Link>
                ) : place.link ? (
                  <a href={place.link} target="_blank" rel="noopener noreferrer" className="eatdrink-link" data-testid={`eatdrink-link-${i}`}>Visit &rarr;</a>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── Admin Panel ── */
function AdminPanel() {
  const [auth, setAuth] = useState(null);
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const headers = useCallback(() => ({
    Authorization: "Basic " + btoa(auth.user + ":" + auth.pass)
  }), [auth]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.get(`${API}/admin/verify`, { headers: { Authorization: "Basic " + btoa(user + ":" + pass) } });
      setAuth({ user, pass });
    } catch {
      setError("Invalid credentials");
    }
  };

  const fetchData = useCallback(async () => {
    if (!auth) return;
    setLoading(true);
    try {
      const [cRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`, { headers: headers() }),
        axios.get(`${API}/admin/stats`, { headers: headers() }),
      ]);
      setContacts(cRes.data);
      setStats(sRes.data);
    } catch {
      setError("Failed to load data");
    }
    setLoading(false);
  }, [auth, headers]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/contacts/${id}`, { headers: headers() });
      setContacts(prev => prev.filter(c => c.id !== id));
      setStats(prev => prev ? { ...prev, total_contacts: prev.total_contacts - 1 } : prev);
    } catch {
      alert("Failed to delete");
    }
  };

  if (!auth) {
    return (
      <div className="admin-wrap" data-testid="admin-login-page">
        <div className="admin-login-card">
          <h1 className="admin-login-title">Castlefreke Admin</h1>
          <p className="admin-login-sub">Sign in to manage contact submissions</p>
          {error && <p className="admin-error" data-testid="admin-login-error">{error}</p>}
          <form onSubmit={login} className="admin-login-form" data-testid="admin-login-form">
            <input className="admin-input" placeholder="Username" value={user} onChange={e => setUser(e.target.value)} required data-testid="admin-username" />
            <input className="admin-input" type="password" placeholder="Password" value={pass} onChange={e => setPass(e.target.value)} required data-testid="admin-password" />
            <button className="admin-btn" type="submit" data-testid="admin-login-btn">Sign in</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-wrap" data-testid="admin-dashboard">
      <div className="admin-container">
        <div className="admin-header">
          <div>
            <h1 className="admin-title">Contact Submissions</h1>
            <p className="admin-subtitle">Manage messages from visitors</p>
          </div>
          <div className="admin-header-actions">
            <a href="/" className="admin-back" data-testid="admin-back-link">Back to site</a>
            <button className="admin-btn-outline" onClick={() => setAuth(null)} data-testid="admin-logout">Sign out</button>
          </div>
        </div>

        {stats && (
          <div className="admin-stats" data-testid="admin-stats">
            <div className="admin-stat-card">
              <span className="admin-stat-val">{stats.total_contacts}</span>
              <span className="admin-stat-label">Total messages</span>
            </div>
            <div className="admin-stat-card">
              <span className="admin-stat-val">{stats.today_contacts}</span>
              <span className="admin-stat-label">Today</span>
            </div>
          </div>
        )}

        {loading ? (
          <p className="admin-loading">Loading...</p>
        ) : contacts.length === 0 ? (
          <p className="admin-empty" data-testid="admin-empty">No contact submissions yet.</p>
        ) : (
          <div className="admin-table-wrap" data-testid="admin-contacts-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(c => (
                  <tr key={c.id} data-testid={`admin-contact-${c.id}`}>
                    <td className="admin-td-date">{new Date(c.timestamp).toLocaleDateString("en-IE", { day: "numeric", month: "short", year: "numeric" })}</td>
                    <td className="admin-td-name">{c.name}</td>
                    <td className="admin-td-email"><a href={`mailto:${c.email}`}>{c.email}</a></td>
                    <td>{c.subject || "—"}</td>
                    <td className="admin-td-msg">{c.message}</td>
                    <td><button className="admin-delete" onClick={() => deleteContact(c.id)} data-testid={`admin-delete-${c.id}`}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Landing Page ── */
function LandingPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="castlefreke" data-testid="app-root">
      <Nav scrolled={scrolled} />
      <Hero />
      <Marquee />
      <About />
      <ThingsToDo />
      <GallerySection />
      <MapSection />
      <VisitSection />
      <EatDrinkSection />
      <JournalSection />
      <footer className="site-footer" data-testid="footer">
        <span>Castlefreke &amp; Long Strand</span>
        <span>West Cork &middot; Ireland</span>
      </footer>
    </div>
  );
}

/* ── App ── */
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/the-castle" element={<CastlePage />} />
        <Route path="/fishers-cross" element={<FishersCrossPage />} />
        <Route path="/journal/:slug" element={<JournalArticle />} />
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
